import React, { useEffect, useState, useCallback } from 'react';
import { SlidersHorizontal, Plus, Trash2, CheckCircle2, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useOrgId } from '../../hooks/useOrgId';
import { useT } from '../../i18n/LanguageContext';
import { schoolApi } from '../../api/school';
import { PageHeader, EmptyState, LoadingState, Card } from '../../components/school/ui';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

const DEFAULT_BANDS = [
  { grade: 'A+', min_percent: 90, max_percent: 100, gpa: 4.0, remark: 'Outstanding' },
  { grade: 'A', min_percent: 80, max_percent: 89.99, gpa: 3.7, remark: 'Excellent' },
  { grade: 'B', min_percent: 70, max_percent: 79.99, gpa: 3.0, remark: 'Very Good' },
  { grade: 'C', min_percent: 60, max_percent: 69.99, gpa: 2.5, remark: 'Good' },
  { grade: 'D', min_percent: 50, max_percent: 59.99, gpa: 2.0, remark: 'Satisfactory' },
  { grade: 'E', min_percent: 40, max_percent: 49.99, gpa: 1.0, remark: 'Pass' },
  { grade: 'F', min_percent: 0, max_percent: 39.99, gpa: 0.0, remark: 'Fail' },
];

export default function GradingScalesPage() {
  const { user } = useAuth();
  const { t } = useT();
  const orgId = useOrgId();
  const [scales, setScales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: '', is_default: false, bands: DEFAULT_BANDS });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!orgId) { setLoading(false); return; }
    try { setLoading(true); setScales(await schoolApi.listGradingScales(orgId)); }
    catch (e) { toast.error('Failed to load'); } finally { setLoading(false); }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const setBand = (i, key, val) => setForm((f) => { const b = [...f.bands]; b[i] = { ...b[i], [key]: key === 'grade' || key === 'remark' ? val : Number(val) }; return { ...f, bands: b }; });
  const addBand = () => setForm((f) => ({ ...f, bands: [...f.bands, { grade: '', min_percent: 0, max_percent: 0, gpa: 0, remark: '' }] }));
  const removeBand = (i) => setForm((f) => ({ ...f, bands: f.bands.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.name.trim()) { toast.error(t('common.required')); return; }
    setSaving(true);
    try { await schoolApi.createGradingScale({ organization_id: orgId, name: form.name, is_default: form.is_default, bands: form.bands }); toast.success(t('common.saved')); setDialogOpen(false); setForm({ name: '', is_default: false, bands: DEFAULT_BANDS }); load(); }
    catch (e) { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  const del = async (id) => { try { await schoolApi.deleteGradingScale(id); toast.success(t('common.deleted')); load(); } catch (e) { toast.error('Failed'); } };

  return (
    <div className="max-w-[1400px] mx-auto">
      <PageHeader title={t('nav.gradingScales')} subtitle="Define grade bands and GPA" icon={SlidersHorizontal}
        actions={<Button onClick={() => { setForm({ name: '', is_default: scales.length === 0, bands: DEFAULT_BANDS }); setDialogOpen(true); }} className="gap-2" data-testid="add-scale-button"><Plus className="w-4 h-4" /> {t('common.add')}</Button>} />

      {loading ? <LoadingState rows={3} /> : scales.length === 0 ? (
        <Card><EmptyState icon={SlidersHorizontal} title={t('common.noData')} actionLabel={t('common.add')} onAction={() => setDialogOpen(true)} /></Card>
      ) : (
        <div className="space-y-4">
          {scales.map((sc) => (
            <Card key={sc.id} className="p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="font-semibold flex items-center gap-2">{sc.name} {sc.is_default && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200"><CheckCircle2 className="w-3 h-3 mr-1" /> {t('common.active')}</Badge>}</p>
                <Button size="icon" variant="ghost" className="text-red-600" onClick={() => del(sc.id)}><Trash2 className="w-4 h-4" /></Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {(sc.bands || []).map((b, i) => (
                  <span key={i} className="inline-flex items-center gap-1 bg-muted/60 rounded px-2.5 py-1 text-xs"><strong>{b.grade}</strong> {b.min_percent}-{b.max_percent}%</span>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="scale-dialog">
          <DialogHeader><DialogTitle>{t('common.add')} {t('nav.gradingScales')}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex items-end gap-4">
              <div className="flex-1"><Label>{t('common.name')} *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="scale-name-input" /></div>
              <label className="flex items-center gap-2 text-sm pb-2"><input type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} /> Default</label>
            </div>
            <div className="border border-border rounded overflow-hidden">
              <table className="w-full text-sm">
                <thead><tr className="bg-muted/60 text-left text-xs"><th className="px-2 py-2">{t('rc.grade')}</th><th className="px-2 py-2">Min %</th><th className="px-2 py-2">Max %</th><th className="px-2 py-2">GPA</th><th className="px-2 py-2">{t('rc.remark')}</th><th /></tr></thead>
                <tbody>
                  {form.bands.map((b, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-1 py-1"><Input className="h-8" value={b.grade} onChange={(e) => setBand(i, 'grade', e.target.value)} /></td>
                      <td className="px-1 py-1"><Input className="h-8" type="number" value={b.min_percent} onChange={(e) => setBand(i, 'min_percent', e.target.value)} /></td>
                      <td className="px-1 py-1"><Input className="h-8" type="number" value={b.max_percent} onChange={(e) => setBand(i, 'max_percent', e.target.value)} /></td>
                      <td className="px-1 py-1"><Input className="h-8" type="number" value={b.gpa} onChange={(e) => setBand(i, 'gpa', e.target.value)} /></td>
                      <td className="px-1 py-1"><Input className="h-8" value={b.remark} onChange={(e) => setBand(i, 'remark', e.target.value)} /></td>
                      <td className="px-1"><Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => removeBand(i)}><X className="w-3.5 h-3.5" /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button size="sm" variant="outline" onClick={addBand}><Plus className="w-3.5 h-3.5 mr-1" /> Add band</Button>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button><Button onClick={save} disabled={saving} data-testid="scale-save-button">{saving ? t('common.saving') : t('common.save')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
